/**
 * Counter — animated number ticker that starts counting when scrolled into view.
 *
 * Uses IntersectionObserver so the animation only fires once, the first time
 * the element becomes visible. Eased with a cubic ease-out over ~1.8s.
 *
 * Extracted from the original monolithic LandingPage for reuse.
 */

import { useEffect, useRef, useState } from 'react';

export interface CounterProps {
  /** Target value to count up to. */
  to: number;
  /** Optional suffix appended after the number (e.g. "+"). */
  suffix?: string;
  /** Optional prefix shown before the number (e.g. "$"). */
  prefix?: string;
}

export function Counter({ to, suffix = '', prefix = '' }: CounterProps) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const scrollProgress = Math.min((now - start) / 1800, 1);
          const eased = 1 - Math.pow(1 - scrollProgress, 3);
          setVal(Math.round(eased * to));
          if (scrollProgress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [to]);

  return (
    <span ref={ref}>
      {prefix}
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

export default Counter;
